import { Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import cn from '../../../lib/cn';

/**
 * Job search field: brand focus ring, clear button and a "/" shortcut to
 * focus it from anywhere on the page.
 */
const JobSearch = ({
  defaultValue = '',
  onSearch,
}: {
  defaultValue?: string;
  onSearch: (value: string) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(defaultValue);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const typing =
        target.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
      if (e.key === '/' && !typing) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const update = (next: string) => {
    setValue(next);
    onSearch(next);
  };

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      className={cn(
        'flex h-12 cursor-text items-center gap-3 rounded-xl border bg-white px-4 transition-[border-color,box-shadow]',
        focused
          ? 'border-theme-primary/60 ring-4 ring-theme-primary/10'
          : 'border-gray-200 hover:border-gray-300',
      )}
    >
      <Search
        className={cn(
          'h-[18px] w-[18px] shrink-0 transition-colors',
          focused ? 'text-theme-primary' : 'text-gray-400',
        )}
        strokeWidth={2}
      />
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => update(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            if (value) update('');
            else inputRef.current?.blur();
          }
        }}
        placeholder="Search by job title, skill or keyword"
        aria-label="Search jobs"
        className="h-full min-w-0 flex-1 bg-transparent text-[15px] text-gray-900 outline-none placeholder:text-gray-400 [&::-webkit-search-cancel-button]:hidden"
      />
      {value ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            update('');
            inputRef.current?.focus();
          }}
          aria-label="Clear search"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-900"
        >
          <X className="h-3.5 w-3.5" strokeWidth={2.5} />
        </button>
      ) : (
        !focused && (
          <kbd className="hidden h-6 min-w-6 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-50 px-1.5 font-sans text-xs font-medium text-gray-500 sm:flex">
            /
          </kbd>
        )
      )}
    </div>
  );
};

export default JobSearch;
